import React from 'react'
import { Dropdown, Button } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import { formatDate } from '../utils/commonUtils'

/**
 * Standard quick select options that can be reused across screens
 */
const standardQuickSelectOptions = [
    {
        label: "Last Week",
        getDates: () => {
            const today = new Date();
            const lastWeek = new Date(today);
            lastWeek.setDate(today.getDate() - 7);
            return { startDate: lastWeek, endDate: today };
        }
    },
    {
        label: "Last 2 Weeks", 
        getDates: () => {
            const today = new Date();
            const twoWeeksAgo = new Date(today);
            twoWeeksAgo.setDate(today.getDate() - 14);
            return { startDate: twoWeeksAgo, endDate: today };
        }
    },
    {
        label: "Last Month",
        getDates: () => {
            const today = new Date();
            const lastMonth = new Date(today);
            lastMonth.setMonth(today.getMonth() - 1);
            return { startDate: lastMonth, endDate: today };
        }
    }
];

/**
 * Extended quick select options with more time ranges
 */
export const extendedQuickSelectOptions = [
    ...standardQuickSelectOptions,
    {
        label: "Last 3 Months",
        getDates: () => {
            const today = new Date();
            const threeMonthsAgo = new Date(today);
            threeMonthsAgo.setMonth(today.getMonth() - 3);
            return { startDate: threeMonthsAgo, endDate: today };
        }
    },
    {
        label: "Last 6 Months",
        getDates: () => {
            const today = new Date();
            const sixMonthsAgo = new Date(today);
            sixMonthsAgo.setMonth(today.getMonth() - 6);
            return { startDate: sixMonthsAgo, endDate: today };
        }
    },
    {
        label: "Last Year",
        getDates: () => {
            const today = new Date();
            const lastYear = new Date(today);
            lastYear.setFullYear(today.getFullYear() - 1);
            return { startDate: lastYear, endDate: today };
        }
    }
];

/**
 * Business-focused quick select options
 */
export const businessQuickSelectOptions = [
    {
        label: "Today",
        getDates: () => {
            const today = new Date();
            return { startDate: today, endDate: today };
        }
    },
    {
        label: "Yesterday",
        getDates: () => {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            return { startDate: yesterday, endDate: yesterday };
        }
    },
    {
        label: "This Week",
        getDates: () => {
            const today = new Date();
            const startOfWeek = new Date(today);
            startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday
            return { startDate: startOfWeek, endDate: today };
        }
    },
    {
        label: "This Month",
        getDates: () => {
            const today = new Date();
            const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            return { startDate: startOfMonth, endDate: today };
        }
    },
    ...standardQuickSelectOptions
];

/**
 * Inventory-specific quick select options (for issued items, returns, etc.)
 */
export const inventoryQuickSelectOptions = [
    {
        label: "Last 7 Days",
        getDates: () => {
            const today = new Date();
            const sevenDaysAgo = new Date(today);
            sevenDaysAgo.setDate(today.getDate() - 7);
            return { startDate: sevenDaysAgo, endDate: today };
        }
    },
    {
        label: "Last 30 Days",
        getDates: () => {
            const today = new Date();
            const thirtyDaysAgo = new Date(today);
            thirtyDaysAgo.setDate(today.getDate() - 30);
            return { startDate: thirtyDaysAgo, endDate: today };
        }
    },
    {
        label: "Last 90 Days",
        getDates: () => {
            const today = new Date();
            const ninetyDaysAgo = new Date(today);
            ninetyDaysAgo.setDate(today.getDate() - 90);
            return { startDate: ninetyDaysAgo, endDate: today };
        }
    },
    {
        label: "This Quarter",
        getDates: () => {
            const today = new Date();
            const quarter = Math.floor(today.getMonth() / 3);
            const startOfQuarter = new Date(today.getFullYear(), quarter * 3, 1);
            return { startDate: startOfQuarter, endDate: today };
        }
    }
];

export const vendorEtaQuickSelectOptions = [
    {
        label: "This Week",
        getDates: () => {
            const today = new Date();
            const endOfWeek = new Date(today);
            endOfWeek.setDate(today.getDate() + (6 - today.getDay()));
            return { startDate: today, endDate: endOfWeek };
        }
    },
    {
        label: "Next 2 Weeks",
        getDates: () => {
            const today = new Date();
            const twoWeeksOut = new Date(today);
            twoWeeksOut.setDate(today.getDate() + 14);
            return { startDate: today, endDate: twoWeeksOut };
        }
    },
    {
        label: "This Month",
        getDates: () => {
            const today = new Date();
            const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            return { startDate: today, endDate: endOfMonth };
        }
    }
];

/**
 * Reusable Date Filter Dropdown Component
 * 
 * Extracted from InventoryHistoryListScreen for consistent date filtering across screens.
 * Includes various predefined quick select option sets that can be exported and used:
 * 
 * - extendedQuickSelectOptions: Standard + 3 months, 6 months, 1 year
 * - businessQuickSelectOptions: Today, Yesterday, This Week/Month + Standard  
 * - inventoryQuickSelectOptions: 7/30/90 days, This Quarter
 * 
 * Default behavior uses the original InventoryHistoryListScreen options:
 * Last Week, Last 2 Weeks, Last Month
 */
function DateFilterDropdown({
    dateRange,
    setDateRange,
    displayedDateRange,
    setDisplayedDateRange,
    onApply,
    dropdownId = "date-filter-dropdown",
    quickSelectOptions = standardQuickSelectOptions,
    allowFutureDates = false,
    label = 'Date'
}) {
    return (
        <Dropdown>
            <Dropdown.Toggle 
                variant="light" 
                id={dropdownId}
                style={{
                    fontWeight: 'bold',
                    padding: '0rem 0.5rem 0.35rem',
                    height: '25px'
                }}
            >
                {displayedDateRange.startDate || displayedDateRange.endDate ?
                    `${formatDate(displayedDateRange.startDate)} - ${formatDate(displayedDateRange.endDate)}` :
                    label}
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ padding: '10px', minWidth: '450px' }}>
                <div 
                    onClick={e => e.stopPropagation()}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                        }
                    }}
                    role="button"
                    tabIndex={0}
                >
                    <div className="d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center gap-2">
                            <small>Start:</small>
                            <DatePicker
                                selected={dateRange.startDate}
                                onChange={date => {
                                    setDateRange(prev => ({ ...prev, startDate: date }));
                                }}
                                selectsStart
                                startDate={dateRange.startDate}
                                endDate={dateRange.endDate}
                                maxDate={allowFutureDates ? undefined : new Date()}
                                className="form-control form-control-sm"
                                placeholderText="Select start date"
                                isClearable
                                shouldCloseOnSelect={true}
                                showMonthDropdown
                                showYearDropdown
                                scrollableYearDropdown
                                dropdownMode="select"
                            />
                        </div>
                        <div style={{ width: '10px' }}></div>
                        <div className="d-flex align-items-center gap-2">
                            <small>End:</small>
                            <DatePicker
                                selected={dateRange.endDate}
                                onChange={date => {
                                    setDateRange(prev => ({ ...prev, endDate: date }));
                                }}
                                selectsEnd
                                startDate={dateRange.startDate}
                                endDate={dateRange.endDate}
                                minDate={dateRange.startDate}
                                className="form-control form-control-sm"
                                placeholderText="Select end date"
                                isClearable
                                maxDate={allowFutureDates ? undefined : new Date()}
                                shouldCloseOnSelect={true}
                                showMonthDropdown
                                showYearDropdown
                                scrollableYearDropdown
                                dropdownMode="select"
                            />
                        </div>
                    </div>
                    <div style={{ height: '10px' }}></div>
                    <div className="mt-2">
                        <Dropdown>
                            <Dropdown.Toggle 
                                variant="outline-secondary" 
                                size="sm"
                                id="quick-date-select"
                            >
                                Quick Select
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {quickSelectOptions.map((option, index) => (
                                    <Dropdown.Item 
                                        key={index}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const dates = option.getDates();
                                            setDateRange(dates);
                                            e.target.closest('.dropdown').querySelector('.dropdown-toggle').click();
                                        }}
                                    >
                                        {option.label}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                    <div className="d-flex gap-2 mt-2 justify-content-between">
                        <div className="d-flex gap-2">
                            <Button 
                                variant="outline-secondary" 
                                size="sm"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDateRange({ startDate: null, endDate: null });
                                }}
                            >
                                Clear Dates
                            </Button>

                            <Button 
                                variant="outline-secondary" 
                                size="sm"
                                onClick={(e) => {
                                    e.target.closest('.dropdown').querySelector('.dropdown-toggle').click();
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                        <Button 
                            variant="primary" 
                            size="sm"
                            onClick={(e) => {
                                e.target.closest('.dropdown').querySelector('.dropdown-toggle').click();
                                setDisplayedDateRange(dateRange);
                                if (onApply) {
                                    onApply();
                                }
                            }}
                        >
                            Set
                        </Button>
                    </div>
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
}

export default DateFilterDropdown;